/**
 * D-EZ-95 / D-EZ-94 — RIVALRIES: feuds, remembered across seasons.
 *
 * A season's `feudThreads` say who feuded and for how long. Read across every
 * season, the same pair turning up again and again is a rivalry — the thing a
 * booth means by "these two have history".
 */

import { sortedSeasons, type HistorySeason } from './source.ts';

export interface Rivalry {
  a: string;
  b: string;
  aName: string;
  bName: string;
  /** Separate feuds between them, ever. */
  feuds: number;
  /** Races a feud between them was live for, ever. */
  races: number;
  /** Seasons they feuded in, ascending. */
  seasons: number[];
  firstSeason: number;
  lastSeason: number;
  /** Live at the end of the latest season read (or now, for a live season). */
  live: boolean;
}

const key = (a: string, b: string): string => (a < b ? `${a}|${b}` : `${b}|${a}`);

export function rivalries(input: readonly HistorySeason[]): Rivalry[] {
  const seasons = sortedSeasons(input);
  const names = new Map<string, string>();
  for (const s of seasons) {
    for (const c of s.roster) names.set(c.driverId, c.name);
    for (const d of s.season.finalStandings.drivers) names.set(d.driverId, d.name);
  }
  const out = new Map<string, Rivalry>();
  const last = seasons[seasons.length - 1];
  for (const s of seasons) {
    for (const t of s.season.feudThreads) {
      const k = key(t.a, t.b);
      const [a, b] = t.a < t.b ? [t.a, t.b] : [t.b, t.a];
      let r = out.get(k);
      if (!r) {
        r = {
          a,
          b,
          aName: names.get(a) ?? a,
          bName: names.get(b) ?? b,
          feuds: 0,
          races: 0,
          seasons: [],
          firstSeason: s.seasonNumber,
          lastSeason: s.seasonNumber,
          live: false,
        };
        out.set(k, r);
      }
      r.feuds += 1;
      r.races += t.races.length;
      if (!r.seasons.includes(s.seasonNumber)) r.seasons.push(s.seasonNumber);
      r.lastSeason = s.seasonNumber;
      if (s === last && (t.endedRace === null || t.endedBy === 'season-ended')) r.live = true;
    }
  }
  return [...out.values()].sort((x, y) => y.feuds - x.feuds || y.races - x.races || y.lastSeason - x.lastSeason);
}

/** The rivalry between two drivers, or null. */
export function rivalryBetween(all: readonly Rivalry[], a: string, b: string): Rivalry | null {
  const k = key(a, b);
  return all.find((r) => key(r.a, r.b) === k) ?? null;
}
